import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Do not redirect while processing an OAuth callback (session_id in URL fragment)
      const isOAuthCallback = window.location.hash?.includes('session_id=');
      if (!isOAuthCallback &&
          window.location.pathname !== '/' && 
          window.location.pathname !== '/login' && 
          window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verify2FA: (code, sessionId) => api.post('/auth/verify-2fa', { code, session_id: sessionId }),
  resend2FACode: (sessionId) => api.post('/auth/2fa/resend', { session_id: sessionId }),
  processSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  redeemAccessKey: (accessKey) => api.post('/auth/redeem-key', { access_key: accessKey }),
  getSubscriptionStatus: () => api.get('/auth/subscription-status'),
  // 2FA Management
  get2FAStatus: () => api.get('/auth/2fa/status'),
  enable2FA: () => api.post('/auth/2fa/enable'),
  confirmEnable2FA: (code, setupId) => api.post('/auth/2fa/confirm-enable', { code, setup_id: setupId }),
  disable2FA: (code, setupId) => api.post('/auth/2fa/disable', { code, setup_id: setupId }),
};

// Admin APIs
export const adminAPI = {
  checkAdmin: () => api.get('/admin/check'),
  getStats: () => api.get('/admin/stats'),
  getAccessKeys: () => api.get('/admin/access-keys'),
  generateAccessKey: (data) => api.post('/admin/access-keys/generate', data),
  deleteAccessKey: (keyId) => api.delete(`/admin/access-keys/${keyId}`),
};

// Company APIs
export const companyAPI = {
  getAll: () => api.get('/companies'),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

// Financial Statement APIs
export const statementAPI = {
  getAll: (companyId) => api.get(`/companies/${companyId}/statements`),
  get: (companyId, statementId) => api.get(`/companies/${companyId}/statements/${statementId}`),
  create: (companyId, data) => api.post(`/companies/${companyId}/statements`, data),
  delete: (companyId, statementId) => api.delete(`/companies/${companyId}/statements/${statementId}`),
};

// AI Insights APIs
export const insightsAPI = {
  generate: (companyId, analysisType = 'comprehensive') => 
    api.post(`/companies/${companyId}/insights`, { company_id: companyId, analysis_type: analysisType }),
  getAll: (companyId) => api.get(`/companies/${companyId}/insights`),
};

// Document APIs
export const documentAPI = {
  upload: (companyId, data) => api.post(`/companies/${companyId}/documents`, data),
  batchUpload: (companyId, documents) => api.post(`/companies/${companyId}/documents/batch`, { documents }),
  getAll: (companyId) => api.get(`/companies/${companyId}/documents`),
  delete: (companyId, documentId) => api.delete(`/companies/${companyId}/documents/${documentId}`),
  analyze: (companyId, analysisType = 'comprehensive') => 
    api.post(`/companies/${companyId}/documents/analyze`, { analysis_type: analysisType }),
  getAnalyses: (companyId) => api.get(`/companies/${companyId}/documents/analyses`),
  getUsage: () => api.get('/users/document-usage'),
};

// Report Export APIs
export const reportAPI = {
  exportExcel: (companyId) => api.get(`/companies/${companyId}/reports/export?format=excel`, { responseType: 'blob' }),
  exportPDF: (companyId) => api.get(`/companies/${companyId}/reports/export?format=pdf`, { responseType: 'blob' }),
  exportCSV: (companyId) => api.get(`/companies/${companyId}/reports/export?format=csv`, { responseType: 'blob' }),
};

// Dashboard APIs
export const dashboardAPI = {
  getOverview: () => api.get('/dashboard/overview'),
  getTrends: (companyId) => api.get(`/dashboard/trends/${companyId}`),
};

// Utility APIs
export const utilsAPI = {
  getExchangeRates: () => api.get('/exchange-rates'),
};

// Subscription APIs
export const subscriptionAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  createCheckout: (data) => api.post('/subscriptions/checkout', data),
  checkStatus: (sessionId) => api.get(`/subscriptions/status/${sessionId}`),
};

// Workers APIs
export const workersAPI = {
  getAll: () => api.get('/workers'),
  getStats: () => api.get('/workers/stats'),
  create: (data) => api.post('/workers', data),
  update: (workerId, data) => api.put(`/workers/${workerId}`, data),
  delete: (workerId) => api.delete(`/workers/${workerId}`),
};

// General Expenses APIs
export const expensesAPI = {
  getAll: () => api.get('/expenses'),
  create: (data) => api.post('/expenses', data),
  delete: (expenseId) => api.delete(`/expenses/${expenseId}`),
};

// Team APIs
export const teamAPI = {
  getAll: () => api.get('/team'),
  getStats: () => api.get('/team/stats'),
  invite: (data) => api.post('/team/invite', data),
  update: (memberId, data) => api.put(`/team/${memberId}`, data),
  remove: (memberId) => api.delete(`/team/${memberId}`),
};

// Client Management APIs
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  get: (clientId) => api.get(`/clients/${clientId}`),
  create: (data) => api.post('/clients', data),
  update: (clientId, data) => api.put(`/clients/${clientId}`, data),
  delete: (clientId) => api.delete(`/clients/${clientId}`),
  // Invoices
  getInvoices: (clientId) => api.get(`/clients/${clientId}/invoices`),
  createInvoice: (clientId, data) => api.post(`/clients/${clientId}/invoices`, data),
  updateInvoice: (clientId, invoiceId, data) => api.put(`/clients/${clientId}/invoices/${invoiceId}`, data),
  deleteInvoice: (clientId, invoiceId) => api.delete(`/clients/${clientId}/invoices/${invoiceId}`),
  // Expenses
  getExpenses: (clientId) => api.get(`/clients/${clientId}/expenses`),
  createExpense: (clientId, data) => api.post(`/clients/${clientId}/expenses`, data),
  deleteExpense: (clientId, expenseId) => api.delete(`/clients/${clientId}/expenses/${expenseId}`),
  // Workers
  getWorkers: (clientId) => api.get(`/clients/${clientId}/workers`),
};

// Book-Keeping APIs
export const bookkeepingAPI = {
  getStats: () => api.get('/bookkeeping/stats'),
  // Ledger
  getLedger: () => api.get('/bookkeeping/ledger'),
  createLedgerEntry: (data) => api.post('/bookkeeping/ledger', data),
  deleteLedgerEntry: (entryId) => api.delete(`/bookkeeping/ledger/${entryId}`),
  // Journal
  getJournal: () => api.get('/bookkeeping/journal'),
  createJournalEntry: (data) => api.post('/bookkeeping/journal', data),
  deleteJournalEntry: (journalId) => api.delete(`/bookkeeping/journal/${journalId}`),
  // Accounts
  getAccounts: () => api.get('/bookkeeping/accounts'),
  // Documents
  getDocuments: () => api.get('/bookkeeping/documents'),
  createDocument: (data) => api.post('/bookkeeping/documents', data),
  getDocumentFile: (documentId) => api.get(`/bookkeeping/documents/${documentId}/file`),
  deleteDocument: (documentId) => api.delete(`/bookkeeping/documents/${documentId}`),
  // Reports
  getTrialBalance: () => api.get('/bookkeeping/trial-balance'),
  // AI Analysis
  analyzeDocuments: (analysisType = 'comprehensive') => api.post('/bookkeeping/analyze', { analysis_type: analysisType }),
  getAnalyses: () => api.get('/bookkeeping/analyses'),
};

export default api;
